import {
  Box,
  Button,
  CustomModalLayout,
  Text,
  TextButton,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FunctionComponent } from 'react';
import { DATA_HOOKS } from '../../../dataHooks';
import css from './index.module.scss';
import { ExternalLinkSmall, ArrowLeftSmall } from 'wix-ui-icons-common';
import { Article, ArticleResolution } from '@wix/answers-api';
import { RoadmapResolutionBadge } from '../../RoadmapResolutionBadge';
import { stripHTML } from '../../../utils/text';

export type RoadmapFeatureRequestSuggestionModalProps = {
  article: Article;
  onSend: () => Promise<void>;
  onVote: (articleId: string) => void;
  onClose: () => void;
  onBack: () => void;
};
export const RoadmapFeatureRequestSuggestionModal: FunctionComponent<
  RoadmapFeatureRequestSuggestionModalProps
> = ({ article, onSend, onVote, onClose, onBack }) => {
  const { t } = useTranslation();

  const content = stripHTML(article.content);

  const modalBody = (
    <Box direction="vertical" gap="SP3">
      <Text size="medium" weight="bold">
        {article.resolution === ArticleResolution.COLLECTING_VOTES
          ? t('feature-request.suggestion.modal.question.vote')
          : t('feature-request.suggestion.modal.question.follow')}
      </Text>
      <Box
        padding={'SP4'}
        className={css.articleSuggestion}
        direction="vertical"
      >
        {article.resolution && (
          <RoadmapResolutionBadge
            resolution={article.resolution}
            showTooltip={true}
            size="tiny"
          />
        )}
        <Text className={css.articleSuggestionTitle} size="small" weight="bold">
          {article.title}
        </Text>
        <Text
          weight="thin"
          maxLines={2}
          ellipsis={true}
          dataHook={DATA_HOOKS.ARTICLE_ITEM_CONTENT}
          className={`${css.articleContent}`}
          size="tiny"
          showTooltip={false}
        >
          {content}
        </Text>
        <TextButton
          className={css.readMoreButton}
          weight="thin"
          size="tiny"
          suffixIcon={<ExternalLinkSmall />}
          as="a"
          href={article.uri}
          target="_blank"
        >
          {t('feature-request.suggestion.modal.read-more')}
        </TextButton>
      </Box>
    </Box>
  );

  const onVoteArticle = () => onVote(article.id);

  return (
    <CustomModalLayout
      secondaryButtonText={t('feature-request.suggestion.modal.send')}
      primaryButtonText={
        article.resolution === ArticleResolution.COLLECTING_VOTES
          ? t('feature-request.suggestion.modal.vote')
          : t('feature-request.suggestion.modal.follow')
      }
      secondaryButtonOnClick={onSend}
      primaryButtonOnClick={onVoteArticle}
      onCloseButtonClick={onClose}
      sideActions={
        <Button
          onClick={onBack}
          dataHook={
            DATA_HOOKS.ROADMAP_FEATURE_REQUEST_SUGGESTION_MODAL_BACK_BUTTON
          }
          priority="secondary"
          size="small"
          prefixIcon={<ArrowLeftSmall />}
        >
          {t('feature-request.suggestion.modal.back')}
        </Button>
      }
      title={t('feature-request.suggestion.modal.title')}
      subtitle={t('feature-request.suggestion.modal.subtitle')}
      content={modalBody}
      width={'600px'}
      dataHook={DATA_HOOKS.ROADMAP_FEATURE_REQUEST_SUGGESTION_MODAL_BODY}
    />
  );
};
